import React from 'react'
import { Phone, Mail, MapPin, ShieldAlert, Clock } from 'lucide-react'
import useMeta from '../hooks/useMeta'
import ContactForm from '../components/ContactForm'
import GoogleMap from '../components/GoogleMap'

export default function Kontakt() {
  // Set metadata
  useMeta({
    title: 'Kontakt · JP Stål og Rør ApS',
    description: 'Kontakt JP Stål og Rør ApS i Tølløse på telefon (+45) 24 46 04 85 eller mail jp@jpstaal.dk for et uforpligtende tilbud på erhvervsløsninger.',
    ogTitle: 'Kontakt · JP Stål og Rør ApS',
    ogDescription: 'Kontakt JP Stål og Rør ApS i Tølløse på telefon (+45) 24 46 04 85 eller mail jp@jpstaal.dk for et uforpligtende tilbud på erhvervsløsninger.',
    ogImage: 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/98e0ec09-72e9-4f17-9c14-50df575a9a1b.jpeg',
    canonicalUrl: 'https://jpstaalogroer.dk/kontakt'
  })

  return (
    <div className="bg-silver-200 text-slate-900 min-h-screen">
      {/* Header Section */}
      <section className="bg-silver-300 border-b border-silver-400 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Kontakt os</span>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-950 uppercase tracking-tight mt-2 mb-4">
            Erhvervsaftaler &amp; Tilbud
          </h1>
          <p className="text-slate-700 text-base max-w-2xl mx-auto font-medium">
            Vi står klar til at rådgive og afgive uforpligtende tilbud på dit næste stål- eller rørprojekt. Bemærk, at vi udelukkende arbejder B2B.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Contact Information */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-2xl font-black text-slate-950 uppercase tracking-tight mb-4">Direkte kontakt</h2>
              <p className="text-slate-700 text-sm leading-relaxed">
                Du kan kontakte os direkte på telefon eller e-mail. For tilbud og prisoverslag anbefaler vi at sende en e-mail med projektbeskrivelse direkte til Jakob Petersen.
              </p>
            </div>

            {/* B2B Alert Box */}
            <div className="bg-amber-500/10 border border-amber-500/30 p-5 rounded-sm flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-bold text-slate-950 uppercase tracking-wider">Kun Erhvervskunder (B2B)</p>
                <p className="text-slate-700 mt-1 leading-relaxed">
                  JP Stål og Rør ApS udfører udelukkende arbejde for virksomheder, entreprenører, industri og forsyningsselskaber. Vi besvarer ikke henvendelser fra private.
                </p>
              </div>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Phone Card */}
              <div className="bg-silver-100 border border-silver-400 p-5 rounded-sm flex items-start gap-4 shadow-sm">
                <div className="p-2.5 bg-amber-500/20 text-amber-700 rounded-sm shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wider font-bold">Ring direkte</p>
                  <a href="tel:+4524460485" className="text-base font-bold text-slate-950 hover:text-amber-600 transition-colors duration-200 whitespace-nowrap">
                    (+45) 24 46 04 85
                  </a>
                  <p className="text-xs text-slate-600 mt-1">Jakob Petersen (Smed &amp; Ejer)</p>
                </div>
              </div>

              {/* Email Card 1 */}
              <div className="bg-silver-100 border border-silver-400 p-5 rounded-sm flex items-start gap-4 shadow-sm">
                <div className="p-2.5 bg-amber-500/20 text-amber-700 rounded-sm shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wider font-bold">E-mail &amp; Tilbud</p>
                  <a href="mailto:jp@jpstaal.dk" className="text-base font-bold text-amber-600 hover:text-amber-700 transition-colors duration-200 whitespace-nowrap">
                    jp@jpstaal.dk
                  </a>
                  <p className="text-xs text-slate-600 mt-1">Direkte kontakt &amp; tilbudsgivning</p>
                </div>
              </div>

              {/* Address Card */}
              <div className="bg-silver-100 border border-silver-400 p-5 rounded-sm flex items-start gap-4 shadow-sm">
                <div className="p-2.5 bg-amber-500/20 text-amber-700 rounded-sm shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wider font-bold">Adresse &amp; CVR</p>
                  <p className="text-sm font-bold text-slate-950 leading-relaxed">
                    Tjørnedevej 127,<br />
                    4340 Tølløse
                  </p>
                  <p className="text-xs text-slate-600 mt-2 font-semibold">CVR-nr: 40160140</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

        </div>
      </section>

      {/* Google Maps Embed in the bottom */}
      <GoogleMap />
    </div>
  )
}
